<template>
  <template v-if="!hasError">
    <slot></slot>
  </template>
  <template v-else>
    <div
      class="flex h-full flex-col items-center justify-between bg-background-fg/5 p-8"
    >
      <IconBrandLogo class="h-auto w-full" />

      <IconExclamationCircle
        :class="[
          hasLongErrorMessage ? 'size-16' : 'size-32',
          'text-background-fg/50',
        ]"
      />

      <div
        :class="[
          hasLongErrorMessage ? 'text-xs' : '',
          'whitespace-pre-wrap py-2 text-center font-bold text-background-fg/50',
        ]"
        >{{ errorMessage ?? "Something went wrong" }}</div
      >

      <div class="space-y-2 self-stretch">
        <ToggleSwitch
          v-model="showDeviceInfo"
          true-label="Device"
          false-label="Network"
        />

        <div
          class="overflow-hidden rounded-lg bg-background"
        >
          <template v-if="showDeviceInfo">
            <MoreMenuItemInfo
              label="Serial Number"
              :value="serialNumber ?? 'Unknown'"
            />

            <MoreMenuItemInfo
              label="App Version"
              :value="appVersion ?? 'Unknown'"
            />
          </template>
          <template v-else>
            <MoreMenuItemInfo
              label="Network Status"
              :value="
                isOnline ? 'connected' : 'not connected'
              "
            />
            <MoreMenuItemInfo
              label="Connection Type"
              :value="connectionType"
            />
            <button
              class="line-clamp-1 p-3 text-xs font-semibold leading-tight text-primary"
              @click="wifiSettingsDrawerIsOpen = true"
              >Open network settings ></button
            >
          </template>
        </div>
      </div>

      <div class="w-full space-y-3">
        <ActionButtonPrimary
          class="w-full"
          @click="exitApp"
        >
          Exit App
        </ActionButtonPrimary>
        <ActionButtonSecondary
          class="w-full bg-background"
          @click="restartApp"
        >
          Restart App
        </ActionButtonSecondary>
        <ActionButtonSecondary
          v-if="isNotProduction"
          class="w-full bg-background"
          @click="restartAppAndResetMid"
        >
          Restart App and Reset MID
        </ActionButtonSecondary>
      </div>
    </div>
  </template>
  <WifiSettingsDrawer
    :is-open="wifiSettingsDrawerIsOpen"
    @close="wifiSettingsDrawerIsOpen = false"
  />
</template>

<script setup lang="ts">
import { IActions } from "@/androidPlugins/IActions"
import { queryClient } from "@/queryClient"
import {
  ApiError,
  FailedToFetchError,
  FetchTimedOutError,
} from "@/services/api"
import { UserVisibleError } from "@/utils/UserVisibleError"
import { useNetwork } from "@vueuse/core"
import {
  IconExclamationCircle,
  IconBrandLogo,
} from "@/icons/"
import ToggleSwitch from "./ToggleSwitch.vue"

const { appVersion, serialNumber } = useDevice()
const { clearTokens } = useAuth()
const { setOverrideMid } = useOverrideMid()
const { setCanHideSplashScreen } = useSplashScreenState()
const { isOnline, type: connectionType } = useNetwork()

const showDeviceInfo = ref(true)
const wifiSettingsDrawerIsOpen = ref(false)
const hasLongErrorMessage = computed(
  () =>
    !!(
      errorMessage.value && errorMessage.value.length >= 50
    )
)

const hasError = ref(false)
const errorMessage = ref<string | null>(null)
const showTpContactDetails = ref(false)

const restartApp = () => {
  hasError.value = false
  errorMessage.value = null
  showTpContactDetails.value = false

  clearTokens()
}

const exitApp = IActions.exitApp

const restartAppAndResetMid = async () => {
  hasError.value = false
  errorMessage.value = null
  showTpContactDetails.value = false
  await setOverrideMid(null)
  queryClient.invalidateQueries()
  clearTokens()
}

onErrorCaptured((error) => {
  if (error instanceof ApiError) {
    // TODO: Decide if we want all 400s caught here, as maybe if not caught by app we should bubble up?
    if (error.status >= 500 || error.status === 401) {
      setCanHideSplashScreen(true)
      hasError.value = true
    }
  } else if (error instanceof FailedToFetchError) {
    return false //Disable further propagation, we dont want sentry catching peoples wifi outages
  } else if (error instanceof FetchTimedOutError) {
    return false // Don't propogate further, we don't want sentry catching wifi outages. Actual error should have be dealt with where api call is made if action wanted.
  } else if (error instanceof UserVisibleError) {
    setCanHideSplashScreen(true)
    hasError.value = true
    errorMessage.value = error.message
    showTpContactDetails.value = error.showTpContactDetails

    return false // Disable further propagation as this was thrown intentionally
  } else {
    setCanHideSplashScreen(true)
    hasError.value = true
  }
})

const isNotProduction =
  import.meta.env.VITE_ENVIRONMENT !== "production"
</script>
